import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useLocale } from '../contexts/LocaleContext';
import type { DayRecord } from '../types';
import { toWeekColumns } from '../utils/history';
const WEEKDAYS_PT = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];
const WEEKDAYS_EN = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
function fillFor(score: number): string {
  if (score === 0) return 'var(--bee-sand-deep)';
  if (score < 40) return 'rgba(46,77,69,0.22)';
  if (score < 70) return 'rgba(46,77,69,0.48)';
  if (score < 100) return 'rgba(16,42,42,0.88)';
  return 'var(--bee-gold)';
}
export function ConsistencyHeatmap({
  records
}: {
  records: DayRecord[];
}) {
  const {
    t,
    locale
  } = useLocale();
  const columns = toWeekColumns(records);
  const weekdays = locale === 'pt-BR' ? WEEKDAYS_PT : WEEKDAYS_EN;
  const dateLocale = locale === 'pt-BR' ? ptBR : undefined;
  return <div data-id="element-650" data-mp="1z0xq3gcjzhzo">
      <div className="flex gap-1" data-id="element-651" data-mp="1z05uw6mvbm9x">
        <div className="flex flex-col gap-[3px] pr-1 pt-[17px]" data-id="element-652" data-mp="1z17ex0lv3j3s">
          {weekdays.map((day, i) => <span key={`${day}-${i}`} className="grid h-[22px] w-[14px] place-items-center text-[9px] font-medium text-muted" aria-hidden="true" data-id="element-653" data-mp="hdjzu22xdajy" data-mpi={i}>
              {day}
            </span>)}
        </div>

        <div className="bee-rail flex flex-1 gap-[3px] overflow-x-auto" data-id="element-654" data-mp="cf09lphatcls">
          {columns.map((column, ci) => {
          const firstRecord = column.find(entry => entry !== null);
          const previousRecord = columns[ci - 1]?.find(entry => entry !== null);
          const showMonth = firstRecord && (ci === 0 || !previousRecord || previousRecord.date.getMonth() !== firstRecord.date.getMonth());
          return <div key={ci} className="flex flex-col gap-[3px]" data-id="element-655" data-mp="3buniv06iykc4" data-mpi={ci}>
              <span className="h-[14px] whitespace-nowrap text-[9px] font-bold uppercase tracking-wide text-muted" data-id="element-656" data-mp="3bx9nos216lqn">
                {showMonth && firstRecord ? format(firstRecord.date, 'MMM', {
                locale: dateLocale
              }) : ''}
              </span>
              {column.map((record, ri) => {
              if (!record) {
                return <span key={ri} className="h-[22px] w-[22px] rounded-[6px]" aria-hidden="true" data-id="element-657" data-mp="1clvo4veoknzt" />;
              }
              if (record.protected) {
                const protectedLabel = t('eng.heatmap.cellProtected', {
                  date: format(record.date, 'dd MMM', {
                    locale: dateLocale
                  })
                });
                return <span key={ri} title={protectedLabel} aria-label={protectedLabel} role="img" className="h-[22px] w-[22px] rounded-[6px] border-[1.5px] border-dashed border-gold bg-sand" data-id="element-658" data-mp="orb3c68s631t" />;
              }
              const label = record.score === 0 ? t('stats.heatmap.cellEmpty', {
                date: format(record.date, 'dd MMM', {
                  locale: dateLocale
                })
              }) : t('stats.heatmap.cell', {
                date: format(record.date, 'dd MMM', {
                  locale: dateLocale
                }),
                score: record.score
              });
              return <span key={ri} title={label} aria-label={label} role="img" className="h-[22px] w-[22px] rounded-[6px] border border-deep/5" style={{
                backgroundColor: fillFor(record.score)
              }} data-id="element-659" data-mp="2co271n7ky0tj" data-mpi={ri} />;
            })}
            </div>;
        })}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-660" data-mp="1aso1zzqxzh90">
        {[{
        key: 'stats.heatmap.none',
        score: 0
      }, {
        key: 'stats.heatmap.low',
        score: 30
      }, {
        key: 'stats.heatmap.high',
        score: 85
      }, {
        key: 'stats.heatmap.perfect',
        score: 100
      }].map(item => <span key={item.key} className="flex items-center gap-1" data-id="element-661" data-mp="1euj3b48vhums">
            <span className="h-[12px] w-[12px] rounded-[4px] border border-deep/10" style={{
          backgroundColor: fillFor(item.score)
        }} aria-hidden="true" data-id="element-662" data-mp="1ewdkc7h0x2ll" />
            <span className="text-[10px] text-muted" data-id="element-663" data-mp="1oqmaioil0jgl">{t(item.key)}</span>
          </span>)}
        {records.some(record => record.protected) && <span className="flex items-center gap-1" data-id="element-664" data-mp="1yjjn6e0w7e7a">
            <span className="h-[12px] w-[12px] rounded-[4px] border border-dashed border-gold bg-sand" aria-hidden="true" data-id="element-665" data-mp="2chrrx8467jt0" />
            <span className="text-[10px] text-muted" data-id="element-666" data-mp="27krl2uzhyd0u">{t('eng.heatmap.protected')}</span>
          </span>}
      </div>
    </div>;
}